import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageLoading } from "../../components/States";

const KINDS = [
  [null, "All"],
  ["host_declaration", "Host declarations"],
  ["driver_consent", "Driver consents"],
  ["booking_acknowledgment", "Booking acknowledgments"],
];
const KIND_LABEL = {
  host_declaration: "Host declaration",
  driver_consent: "Driver consent",
  booking_acknowledgment: "Booking ack",
};

// Declaration & consent audit (admin only): user reference, role, listing or
// booking reference, declaration/Terms versions, status and timestamp.
// Data-minimized server payload — no identity documents or extra PII.
export default function AdminConsents() {
  const [type, setType] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setData(null);
    setError(null);
    api.adminConsents(type).then(setData).catch((e) => setError(e.message));
  }, [type]);
  useEffect(load, [load]);

  return (
    <div data-testid="admin-consents-page">
      <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Consent record type">
        {KINDS.map(([v, l]) => (
          <button key={l} type="button" onClick={() => setType(v)} data-testid={`consent-filter-${v || "all"}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${type === v ? "bg-[#E50914] border-[#E50914] text-white" : "bg-[#1F2328] border-[#27272A] text-zinc-300 hover:border-[#3F3F46]"}`}>
            {l}
          </button>
        ))}
      </div>

      {data?.current_versions && (
        <p className="mt-4 text-[11px] text-zinc-500" data-testid="consent-versions">
          Current versions — host declaration {data.current_versions.host_declaration} · driver declaration {data.current_versions.driver_declaration} · Terms {data.current_versions.terms}
        </p>
      )}

      {error && <p className="mt-6 text-sm text-red-400" role="alert" data-testid="consents-error">{error}</p>}
      {!data && !error && <div className="mt-8"><PageLoading label="Loading consent records…" /></div>}
      {data && data.records.length === 0 && (
        <p className="mt-8 text-sm text-zinc-500" data-testid="consents-empty">No consent records yet.</p>
      )}
      {data && data.records.length > 0 && (
        <div className="mt-5 overflow-x-auto rounded-xl border border-[#27272A]" data-testid="consents-table">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#27272A] text-zinc-500 uppercase tracking-wider text-[10px]">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Declaration v.</th>
                <th className="px-4 py-3">Terms v.</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Accepted at</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F2328]">
              {data.records.map((r, i) => (
                <tr key={`${r.kind}-${r.user_id}-${r.spot_id || r.booking_id || i}`} className="text-zinc-300" data-testid={`consent-row-${i}`}>
                  <td className="px-4 py-3 font-semibold text-zinc-100">{KIND_LABEL[r.kind] || r.kind}</td>
                  <td className="px-4 py-3">
                    <span className="block text-zinc-200">{r.user_name || "—"}</span>
                    <span className="block text-[10px] text-zinc-600">{r.user_email || r.user_id}</span>
                  </td>
                  <td className="px-4 py-3 capitalize">{r.role}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-zinc-400">
                    {r.booking_id || r.spot_id || "—"}
                    {r.relationship ? <span className="block text-[10px] text-zinc-600 font-sans">{r.relationship}</span> : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px]">{r.declaration_version || "—"}</td>
                  <td className="px-4 py-3 font-mono text-[11px]">{r.terms_version || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{r.accepted_at ? new Date(r.accepted_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
